import { Axios, AxiosError, AxiosResponse } from 'axios'
import { RemoteConfig } from './remote.config'

export enum QuestionAuthoringStatus {
	DRAFT = "DRAFT",
	PUBLISHED = "PUBLISHED",
	DISCARDED = "DISCARDED",
}

export enum QuestionEvidenceRequirement {
	ALWAYS = "ALWAYS",
	SOMETIMES = "SOMETIMES",
	NEVER = "NEVER",
	NOT_APPLICABLE = "NOT_APPLICABLE",
}

export enum QuestionType {
	YES_NO_PARTIAL = "YES_NO_PARTIAL",
}

export enum QuestionnaireEntryType {
	QUESTION = "QUESTION",
	QUESTION_SET = "QUESTION_SET",
}

export interface Questionnaire {
	id: string

	created: Date
	created_by: string
	updated: Date
	updated_by: string

	owned_by: string

	name: string
	latest_version?: string
	has_draft: boolean

	versions: QuestionnaireVersion[]
}

export interface CreateQuestionnaireData {
	owned_by: string
	name: string
}

export interface UpdateQuestionnaireData {
	name: string
}

export interface QuestionnaireVersion {
	id: string
	questionnaire_id: string

	created: Date
	created_by: string
	updated: Date
	updated_by: string

	version?: number
	comment: string
	status: QuestionAuthoringStatus

	entries: QuestionnaireEntry[]
}

export interface CreateQuestionnaireVersionData {
	questionnaire_id: string
	comment: string
	entries: CreateQuestionnaireEntryData[]
}

export interface UpdateQuestionnaireVersionData {
	comment: string
}

export interface QuestionnaireEntry {
	id: string
	questionnaire_id: string
	entry_type: QuestionnaireEntryType
	question?: QuestionVersion
	question_set?: QuestionSetVersion
}

export interface CreateQuestionnaireEntryData {
	questionnaire_id: string
	entry_type: QuestionnaireEntryType
	question?: string
	question_set?: string
}

export interface QuestionSet {
	id: string

	created: Date
	created_by: string
	updated: Date
	updated_by: string

	owned_by: string

	name: string
	latest_version?: string
	has_draft: boolean

	versions: QuestionSetVersion[]
}

export interface CreateQuestionSetData {
	owned_by: string
	name: string
}

export interface UpdateQuestionSetData {
	name: string
}

export interface QuestionSetVersion {
	id: string
	question_set_id: string

	created: Date
	created_by: string
	updated: Date
	updated_by: string

	version?: number
	comment: string
	status: QuestionAuthoringStatus

	entries: QuestionSetEntry[]
}

export interface CreateQuestionSetVersionData {
	question_set_id: string
	comment: string
	entries: CreateQuestionSetEntryData[]
}

export interface UpdateQuestionSetVersionData {
	comment: string
}

export interface QuestionSetEntry {
	id: string
	question_set_id: string
	question: QuestionVersion
}

export interface CreateQuestionSetEntryData {
	question_set_id: string
	question: string
}

export interface Question {
	id: string

	created: Date
	created_by: string
	updated: Date
	updated_by: string

	owned_by: string

	latest_version?: string
	has_draft: boolean

	versions: QuestionVersion[]
}

export interface CreateQuestionData {
	owned_by: string
}

export interface QuestionVersion {
	id: string
	question_id: string

	created: Date
	created_by: string
	updated: Date
	updated_by: string

	version?: number
	comment: string
	status: QuestionAuthoringStatus

	text: string
	question_type: QuestionType
	question_evidence_requirement: QuestionEvidenceRequirement
}

export interface CreateQuestionVersionData {
	question_id: string

	comment: string
	status: QuestionAuthoringStatus

	text: string
	question_type: QuestionType
	question_evidence_requirement: QuestionEvidenceRequirement
}

export interface UpdateQuestionVersionData {
	comment: string
	text: string
	question_type: QuestionType
	question_evidence_requirement: QuestionEvidenceRequirement
}

function getEntityFunc<EntityType, URLArgs>(urlFunc: (location: URLArgs) => string) {
	return (args: URLArgs, callback: (data?: EntityType, resp?: AxiosResponse, err?: AxiosError) => void) => {
		const client = new Axios();
		const url = new URL(urlFunc(args), RemoteConfig.url);
		const result = client.get(url.toString(), {withCredentials:true}).then((resp) => {
			let data: EntityType = JSON.parse(resp.data)
			callback(data, resp)
		})
	}
}

function postEntityFunc<EntityType, PostData, URLArgs>(urlFunc: (location: URLArgs) => string) {
	return (args: URLArgs, data: PostData, callback: (data?: EntityType, resp?: AxiosResponse, err?: AxiosError) => void) => {
		const client = new Axios();
		const url = new URL(urlFunc(args), RemoteConfig.url);
		const result = client.post(url.toString(), JSON.stringify(data), {withCredentials:true, headers: {
			'Content-Type': 'application/json'
		}}).then((resp) => {
			let data: EntityType = JSON.parse(resp.data)
			callback(data, resp)
		})
	}
}

function deleteEntityFunc<EntityType, URLArgs>(urlFunc: (location: URLArgs) => string) {
	return (args: URLArgs, callback: (data?: EntityType, resp?: AxiosResponse, err?: AxiosError) => void) => {
		const client = new Axios();
		const url = new URL(urlFunc(args), RemoteConfig.url);
		const result = client.delete(url.toString(), {withCredentials:true}).then((resp) => {
			let data: EntityType = JSON.parse(resp.data)
			callback(data, resp)
		})
	}
}
export interface QuestionnaireSearchLocation {
	organization: string
}
export interface GenericQuestionnaireLocation {}
export interface SpecificQuestionnaireLocation {
	questionnaire: string
}

export const getQuestionnaire = getEntityFunc<Questionnaire, SpecificQuestionnaireLocation>((location) => {
	return `/api/v0/questionnaire/${location.questionnaire}`
})

export const listQuestionnaires = getEntityFunc<Questionnaire[], QuestionnaireSearchLocation>((location) => {
	return `/api/v0/questionnaire/?organization_id=${location.organization}`
})

export const createQuestionnaire = postEntityFunc<Questionnaire, CreateQuestionnaireData, GenericQuestionnaireLocation>(() => {
	return `/api/v0/questionnaire/`
})

export const updateQuestionnaire = postEntityFunc<Questionnaire, UpdateQuestionnaireData, SpecificQuestionnaireLocation>((location) => {
	return `/api/v0/questionnaire/${location.questionnaire}`
})

export const deleteQuestionnaire = deleteEntityFunc<Questionnaire, SpecificQuestionnaireLocation>((location) => {
	return `/api/v0/questionnaire/${location.questionnaire}`
})


export interface GenericQuestionnaireVersionLocation {
	questionnaire: string
}
export interface SpecificQuestionnaireVersionLocation {
	questionnaire: string
	version: string
}

export const getQuestionnaireVersion = getEntityFunc<QuestionnaireVersion, SpecificQuestionnaireVersionLocation>((location) => {
	return `/api/v0/questionnaire/${location.questionnaire}/version/${location.version}`
})

export const listQuestionnaireVersions = getEntityFunc<QuestionnaireVersion[], GenericQuestionnaireVersionLocation>((location) => {
	return `/api/v0/questionnaire/${location.questionnaire}/version`
})

export const createQuestionnaireVersion = postEntityFunc<QuestionnaireVersion, CreateQuestionnaireVersionData, GenericQuestionnaireVersionLocation>((location) => {
	return `/api/v0/questionnaire/${location.questionnaire}/version`
})

export const updateQuestionnaireVersion = postEntityFunc<QuestionnaireVersion, UpdateQuestionnaireVersionData, SpecificQuestionnaireVersionLocation>((location) => {
	return `/api/v0/questionnaire/${location.questionnaire}/version/${location.version}`
})

export const publishQuestionnaireVersion = postEntityFunc<QuestionnaireVersion, void, SpecificQuestionnaireVersionLocation>((location) => {
	return `/api/v0/questionnaire/${location.questionnaire}/version/${location.version}/publish`
})


export interface GenericQuestionnaireEntryLocation {
	questionnaire: string
	version: string
}
export interface SpecificQuestionnaireEntryLocation {
	questionnaire: string
	version: string
	entry: string
}

export const getQuestionnaireEntry = getEntityFunc<QuestionnaireEntry, SpecificQuestionnaireEntryLocation>((location) => {
	return `/api/v0/questionnaire/${location.questionnaire}/version/${location.version}/entry/${location.entry}`
})

export const listQuestionnaireEntries = getEntityFunc<QuestionnaireEntry[], GenericQuestionnaireEntryLocation>((location) => {
	return `/api/v0/questionnaire/${location.questionnaire}/version/${location.version}/entry`
})

export const createQuestionnaireEntry = postEntityFunc<QuestionnaireEntry, CreateQuestionnaireEntryData, GenericQuestionnaireEntryLocation>((location) => {
	return `/api/v0/questionnaire/${location.questionnaire}/version/${location.version}/entry`
})

export const deleteQuestionnaireEntry = deleteEntityFunc<QuestionnaireEntry, SpecificQuestionnaireEntryLocation>((location) => {
	return `/api/v0/questionnaire/${location.questionnaire}/version/${location.version}/entry/${location.entry}`
})

export interface QuestionSetListLocation {
	organization: string
}
export interface QuestionSetSearchLocation {
	organization: string
	searchText: string
}
export interface GenericQuestionSetLocation {}
export interface SpecificQuestionSetLocation {
	questionSet: string
}

export const getQuestionSet = getEntityFunc<QuestionSet, SpecificQuestionSetLocation>((location) => {
	return `/api/v0/question_set/${location.questionSet}`
})

export const listQuestionSets = getEntityFunc<QuestionSet[], QuestionSetListLocation>((location) => {
	return `/api/v0/question_set/?organization_id=${location.organization}`
})

export const searchQuestionSets = getEntityFunc<QuestionSet[], QuestionSetSearchLocation>((location) => {
	return `/api/v0/question_set/?organization_id=${location.organization}&search_text=${location.searchText}`
})

export const createQuestionSet = postEntityFunc<QuestionSet, CreateQuestionSetData, GenericQuestionSetLocation>(() => {
	return `/api/v0/question_set/`
})

export const updateQuestionSet = postEntityFunc<QuestionSet, UpdateQuestionSetData, SpecificQuestionSetLocation>((location) => {
	return `/api/v0/question_set/${location.questionSet}`
})

export const deleteQuestionSet = deleteEntityFunc<QuestionSet, SpecificQuestionSetLocation>((location) => {
	return `/api/v0/question_set/${location.questionSet}`
})


export interface GenericQuestionSetVersionLocation {
	questionSet: string
}
export interface SpecificQuestionSetVersionLocation {
	questionSet: string
	version: string
}

export const getQuestionSetVersion = getEntityFunc<QuestionSetVersion, SpecificQuestionSetVersionLocation>((location) => {
	return `/api/v0/question_set/${location.questionSet}/version/${location.version}`
})

export const listQuestionSetVersions = getEntityFunc<QuestionSetVersion[], GenericQuestionSetVersionLocation>((location) => {
	return `/api/v0/question_set/${location.questionSet}/version`
})

export const createQuestionSetVersion = postEntityFunc<QuestionSetVersion, CreateQuestionSetVersionData, GenericQuestionSetVersionLocation>((location) => {
	return `/api/v0/question_set/${location.questionSet}/version`
})

export const updateQuestionSetVersion = postEntityFunc<QuestionSetVersion, UpdateQuestionSetVersionData, SpecificQuestionSetVersionLocation>((location) => {
	return `/api/v0/question_set/${location.questionSet}/version/${location.version}`
})

export const publishQuestionSetVersion = postEntityFunc<QuestionSetVersion, void, SpecificQuestionSetVersionLocation>((location) => {
	return `/api/v0/question_set/${location.questionSet}/version/${location.version}/publish`
})


export interface GenericQuestionSetEntryLocation {
	questionSet: string
	version: string
}
export interface SpecificQuestionSetEntryLocation {
	questionSet: string
	version: string
	entry: string
}

export const getQuestionSetEntry = getEntityFunc<QuestionSetEntry, SpecificQuestionSetEntryLocation>((location) => {
	return `/api/v0/question_set/${location.questionSet}/version/${location.version}/entry/${location.entry}`
})

export const listQuestionSetEntries = getEntityFunc<QuestionSetEntry[], GenericQuestionSetEntryLocation>((location) => {
	return `/api/v0/question_set/${location.questionSet}/version/${location.version}/entry`
})

export const createQuestionSetEntry = postEntityFunc<QuestionSetEntry, CreateQuestionSetEntryData, GenericQuestionSetEntryLocation>((location) => {
	return `/api/v0/question_set/${location.questionSet}/version/${location.version}/entry`
})

export const deleteQuestionSetEntry = deleteEntityFunc<QuestionSetEntry, SpecificQuestionSetEntryLocation>((location) => {
	return `/api/v0/question_set/${location.questionSet}/version/${location.version}/entry/${location.entry}`
})

export interface QuestionListLocation {
	organization: string
}
export interface QuestionSearchLocation {
	organization: string
	searchText: string
}
export interface GenericQuestionLocation {}
export interface SpecificQuestionLocation {
	question: string
}

export const getQuestion = getEntityFunc<Question, SpecificQuestionLocation>((location) => {
	return `/api/v0/question/${location.question}`
})

export const listQuestions = getEntityFunc<Question[], QuestionListLocation>((location) => {
	return `/api/v0/question/?organization_id=${location.organization}`
})

export const searchQuestions = getEntityFunc<Question[], QuestionSearchLocation>((location) => {
	return `/api/v0/question/?organization_id=${location.organization}&search_text=${location.searchText}`
})

export const createQuestion = postEntityFunc<Question, CreateQuestionData, GenericQuestionLocation>(() => {
	return `/api/v0/question/`
})

export const deleteQuestion = deleteEntityFunc<Question, SpecificQuestionLocation>((location) => {
	return `/api/v0/question/${location.question}`
})


export interface GenericQuestionVersionLocation {
	question: string
}
export interface SpecificQuestionVersionLocation {
	question: string
	version: string
}

export const getQuestionVersion = getEntityFunc<QuestionVersion, SpecificQuestionVersionLocation>((location) => {
	return `/api/v0/question/${location.question}/version/${location.version}`
})

export const listQuestionVersions = getEntityFunc<QuestionVersion[], GenericQuestionVersionLocation>((location) => {
	return `/api/v0/question/${location.question}/version`
})

export const createQuestionVersion = postEntityFunc<QuestionVersion, CreateQuestionVersionData, GenericQuestionVersionLocation>((location) => {
	return `/api/v0/question/${location.question}/version`
})

export const updateQuestionVersion = postEntityFunc<QuestionVersion, UpdateQuestionVersionData, SpecificQuestionVersionLocation>((location) => {
	return `/api/v0/question/${location.question}/version/${location.version}`
})

export const publishQuestionVersion = postEntityFunc<QuestionVersion, void, SpecificQuestionVersionLocation>((location) => {
	return `/api/v0/question/${location.question}/version/${location.version}/publish`
})
